import { inject, Injectable, LOCALE_ID } from '@angular/core';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';

import { environment } from '@env/environment';

import { WINDOW, APP_HOST } from '../common/tokens';
import { isEmpty } from '../helpers';

import { META_SETTINGS } from './meta.token';

@Injectable({
  providedIn: 'root'
})
export class MetaService {
  private readonly meta = inject(Meta);
  private readonly title = inject(Title);

  public hasTitle = false;

  private readonly synonyms: Record<string, string> = {
    'description': 'og:description',
    'author': 'og:author',
    'publisher': 'og:publisher'
  };

  private readonly window = inject(WINDOW);
  private readonly settings = inject(META_SETTINGS);
  private readonly localeId = inject(LOCALE_ID);
  private readonly appHost = inject(APP_HOST, { optional: true });

  setDefaults(currentUrl: string): void {
    if(null !== this.settings.defaults) {
      this.setTags(this.settings.defaults);
    }

    if(null !== this.settings.defaultTitle) {
      this.setTitle(this.settings.defaultTitle, true);
    }

    this.setUrlTag(currentUrl);
  }

  setDefaultTitle(): void {
    if(null !== this.settings.defaultTitle) {
      this.setTitle(this.settings.defaultTitle, true);
    }
  }

  setTitle(title: string|null, rawMode: boolean = false): void {
    const {
      titlePositioning,
      applicationName,
      titleSeparator
    } = this.settings;

    if(!isEmpty(title)) {
      if(!rawMode) {
        if('append' === titlePositioning) {
          title = applicationName + titleSeparator + title;
        } else if('prepend' === titlePositioning) {
          title = title + titleSeparator + applicationName;
        }
      }
    } else {
      title = applicationName;
    }

    if(!title) {
      return;
    }

    this.hasTitle = true;
    this.title.setTitle(title);
    this.setTag('og:title', title);
  }

  setTag(key: string, content: string): void {
    if('title' === key) {
      return;
    }

    if(this.synonyms[key]) {
      this.setTag(this.synonyms[key], content);
    }

    let property: string;

    if(key.lastIndexOf('og:', 0) === 0) {
      property = 'property';
    } else {
      property = 'name';
    }

    const definition: MetaDefinition = {
      content,
      [property]: key
    };

    if(!this.meta.getTag(`${property}='${key}'`)) {
      this.meta.addTag(definition);
    } else {
      this.meta.updateTag(definition);
    }
  }

  setTags(definition: MetaDefinition): void {
    for(const key in definition) {
      this.setTag(key, definition[key]);
    }
  }

  setLocaleTags(): void {
    // const localeFromMeta: string = this.meta.getTag("property='og:locale'").content;

    let alternateLocales: string[] = [];

    environment.supportedLocales
      .filter((locale: string) => locale !== this.localeId)
      .forEach((locale: string) => alternateLocales.push(locale));

    alternateLocales = alternateLocales.map((locale: string) => locale.replace(/-/g, '_'));

    this.setTag('og:locale', this.localeId.replace(/-/g, '_'));
    this.setTag('og:locale:alternate', alternateLocales.join(','));
  }

  private setUrlTag(currentUrl: string): void {
    const baseUrl = `https://${ this.window?.location.host || this.appHost }/${ this.localeId}`;
    const url = `${baseUrl}${currentUrl}`.replace(/(https?:\/\/)|(\/)+/g, '$1$2').replace(/\/$/g, '');

    this.setTag('og:url', url || '/');
  }
}
